import { useState, useEffect, useCallback } from "react";
import axiosInstance from "services/httpInterceptor";

/**
 * useIndustryBenchmark
 *
 * Fetches v2 engagement stats for all competitor IDs + the client company
 * in parallel, then computes industry averages across competitors.
 *
 * Returns:
 *   clientStats      — DigitalPresenceEngagementDTO for the client
 *   industryAvg      — averaged stats across all competitors
 *   competitorStats  — array of raw per-competitor DTOs (with .id and .name)
 *   loading          — true while any fetch is in flight
 *   error            — true if the client fetch failed
 */
const fetchStats = (platform, companyId, presetKey) =>
  axiosInstance
    .get(`/v1/frontend/${platform}/v2/general/stats`, {
      params: { companyId, preset: presetKey.toUpperCase() },
    })
    .then((r) => r.data)
    .catch(() => null);

const avg = (values) => {
  const valid = values.filter((v) => v != null && !isNaN(v));
  if (valid.length === 0) return null;
  return valid.reduce((s, v) => s + v, 0) / valid.length;
};

const sum = (values) => {
  const valid = values.filter((v) => v != null && !isNaN(v));
  if (valid.length === 0) return null;
  return valid.reduce((s, v) => s + v, 0);
};

/** Mode of an array — the most frequent value */
const mode = (values) => {
  const valid = values.filter(Boolean);
  if (valid.length === 0) return null;
  const freq = {};
  valid.forEach((v) => { freq[v] = (freq[v] || 0) + 1; });
  return Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0];
};

const useIndustryBenchmark = (platform, clientId, allCompetitorIds, presetKey) => {
  const [clientStats, setClientStats] = useState(null);
  const [industryAvg, setIndustryAvg] = useState(null);
  const [competitorStats, setCompetitorStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const run = useCallback(async () => {
    if (!clientId || !presetKey || allCompetitorIds.length === 0) return;

    setLoading(true);
    setError(false);

    try {
      const [cliResult, ...cmpResults] = await Promise.all([
        fetchStats(platform, clientId, presetKey),
        ...allCompetitorIds.map((id) => fetchStats(platform, id, presetKey)),
      ]);

      if (!cliResult) setError(true);
      setClientStats(cliResult);

      const valid = cmpResults.filter(Boolean);
      setCompetitorStats(valid);

      if (valid.length > 0) {
        setIndustryAvg({
          totalPosts: Math.round(avg(valid.map((s) => s.totalPosts)) ?? 0),
          totalEngagements: Math.round(avg(valid.map((s) => s.totalEngagements)) ?? 0),
          avgEngagementsPerPost: avg(valid.map((s) => s.avgEngagementsPerPost)),
          bestPostEngagement: Math.round(avg(valid.map((s) => s.bestPostEngagement)) ?? 0),
          mostCommonContentCategory: mode(valid.map((s) => s.mostCommonContentCategory)),
          // Extra: total posts across all competitors (for context)
          totalPostsSum: sum(valid.map((s) => s.totalPosts)),
          competitorCount: valid.length,
        });
      } else {
        setIndustryAvg(null);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [platform, clientId, allCompetitorIds, presetKey]);

  useEffect(() => {
    run();
  }, [run]);

  return { clientStats, industryAvg, competitorStats, loading, error };
};

export default useIndustryBenchmark;
